'use client'

import { FormRadio, FormRadioGroup, useFormContext, useStoreState } from '@ariakit/react'
import { ComponentProps, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { Select, SelectOption, TypeStyleHeadlineMicro } from '@syconium/little-miss-figgy'

import { SizeChart } from '../../../components/pages/products/ProductPage/DetailsSection/SizesSection/SizeChart'
import { trackEvent } from '../../../lib/analytics'
import { usePrettySizeName } from '../../_hooks/usePrettySizeName'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormError } from '../form/FormError'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormState,
  VariantSelectionFromSoldOutMessage,
  VariantSelectionInvisibleInput,
  VariantSelectionOption,
  VariantSelectionOptionsRow,
  variantSelectionFormGroupLabelTypographyCss,
} from './VariantSelectionForm'

const useVariantSelectionSizeField = ({ autoSelectFirst }: { autoSelectFirst: boolean }) => {
  const { selectedVariant, product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()
  const value = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''
  const option = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
  const displayValue = option?.size.displayValue ?? value
  const renderSoldOutLabel = option?.outOfStock && variantGroup.availability === 'AVAILABLE'

  if (
    (product.options.sizes?.length === 1 || autoSelectFirst) &&
    value.length === 0 &&
    selectedVariant?.size?.id
  ) {
    formContext?.setValue('size', selectedVariant.size.id)
  }

  const prettyDisplayValue = usePrettySizeName({
    id: value,
    displayValue: displayValue,
  })

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const setValue = useCallback(
    (newSize: string) => {
      formContext!.setValue('size', newSize)
    },
    [formContext]
  )

  const error = useStoreState(formContext, () => formContext?.getError('size'))

  const renderAsDisplayValue = useCallback(
    (value: string) => {
      const sizeOption = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
      const sizeDisplayText = sizeOption?.outOfStock
        ? `${sizeOption.size.displayValue} ${translations.giftCard.dropdownSoldOutOptionText}`
        : sizeOption
        ? sizeOption.size.displayValue
        : value
      return <TypeStyleHeadlineMicro>{sizeDisplayText}</TypeStyleHeadlineMicro>
    },
    [product.options.sizes, translations.giftCard.dropdownSoldOutOptionText]
  )

  return {
    labelText: translations.quickBuy.modal.size,
    labelDisplayValue: prettyDisplayValue,
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    error,
  }
}

type VariantSelectionSizeRadioGroupProps = {
  sizeChart: ComponentProps<typeof SizeChart>['sizeChart']
  callToActionColor?: React.CSSProperties['color']
} & VariantSelectionFieldProps

export const VariantSelectionSizeRadioGroup = ({
  sizeChart,
  onChange,
  callToActionColor,
}: VariantSelectionSizeRadioGroupProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { labelText, labelDisplayValue, soldOutLabelPostfix, value } = useVariantSelectionSizeField(
    { autoSelectFirst: false }
  )

  return (
    <div>
      <FormRadioGroup>
        <StyledFormGroupLabelWrapper>
          <VariantSelectionFormGroupLabel>
            {labelText}{' '}
            {labelDisplayValue.length > 0 ? (
              <VariantSelectionFieldValueDisplay>
                {labelDisplayValue}
                {soldOutLabelPostfix}
              </VariantSelectionFieldValueDisplay>
            ) : null}
          </VariantSelectionFormGroupLabel>
          {sizeChart ? <StyledSizeChart sizeChart={sizeChart} /> : null}
        </StyledFormGroupLabelWrapper>
        <VariantSelectionOptionsRow>
          {product.options.sizes?.map(sizeOption => {
            const size = sizeOption.size
            const disabled = sizeOption.outOfStock
            return (
              <FormRadio
                name={'size'}
                key={size.id}
                value={size.id}
                required
                render={htmlInputProps => {
                  return (
                    <label
                      htmlFor={htmlInputProps.id}
                      {...trackEvent({
                        action: 'click',
                        category: trackingCategory,
                        label: 'Size',
                        value: size.id,
                      })}
                    >
                      <VariantSelectionInvisibleInput
                        {...htmlInputProps}
                        aria-label={size.displayValue}
                        onChange={changeEvent => {
                          htmlInputProps.onChange?.(changeEvent)
                          if (changeEvent.target.checked) {
                            onChange?.(size.id)
                          }
                        }}
                      />
                      <VariantSelectionOption
                        aria-hidden={true}
                        aria-label={size.displayValue}
                        isSelected={value === size.id}
                        soldOut={disabled}
                        accentColor={callToActionColor}
                        as='div'
                      >
                        {size.displayValue}
                      </VariantSelectionOption>
                    </label>
                  )
                }}
              />
            )
          })}
        </VariantSelectionOptionsRow>
        <FormError name='size' />
      </FormRadioGroup>
    </div>
  )
}

export const VariantSelectionSizeSelect = ({
  onChange: onChangeProp,
}: VariantSelectionFieldProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { labelText, setValue, renderAsDisplayValue, value, error } = useVariantSelectionSizeField({
    autoSelectFirst: true,
  })
  const onChange = useCallback(
    (newValue: string) => {
      onChangeProp?.(newValue)
      setValue(newValue)
    },
    [onChangeProp, setValue]
  )

  return (
    <div>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'size'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!!error}
        heading={labelText}
      >
        {product.options.sizes?.map(sizeOption => {
          const size = sizeOption.size
          return (
            <SelectOption
              key={size.id}
              value={size.id}
              {...trackEvent({
                action: 'click',
                category: trackingCategory,
                label: 'Size',
                value: size.id,
              })}
            >
              {renderAsDisplayValue(size.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='size' />
    </div>
  )
}

const StyledFormGroupLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${o => o.theme.spacing(2)};
  align-items: flex-start;
`

const StyledSizeChart = styled(SizeChart)`
  ${variantSelectionFormGroupLabelTypographyCss};
  flex-shrink: 0;
`
